import { forwardRef, type HTMLAttributes } from "react";

import { cn } from "../lib/utils";

export interface SignalGaugeProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: number | null | undefined;
  unit?: string;
  showBar?: boolean;
}

function getSignalColor(value: number): string {
  if (value > -20) return "text-success";
  if (value >= -27) return "text-warning";
  return "text-destructive";
}

function getBarColor(value: number): string {
  if (value > -20) return "bg-success";
  if (value >= -27) return "bg-warning";
  return "bg-destructive";
}

function getBarWidth(value: number): string {
  const clamped = Math.max(-30, Math.min(0, value));
  return `${Math.round(((clamped + 30) / 30) * 100)}%`;
}

const SignalGauge = forwardRef<HTMLDivElement, SignalGaugeProps>(
  ({ className, label, value, unit = "dBm", showBar = false, ...props }, ref) => {
    const hasValue = value !== null && value !== undefined;
    return (
      <div ref={ref} className={cn("flex flex-col gap-1", className)} {...props}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
          <span className={cn("text-sm font-semibold tabular-nums", hasValue && getSignalColor(value as number), !hasValue && "text-muted-foreground")}>
            {hasValue ? `${value} ${unit}` : "N/A"}
          </span>
        </div>
        {showBar && hasValue && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className={cn("h-full rounded-full transition-all duration-300", getBarColor(value as number))} style={{ width: getBarWidth(value as number) }} />
          </div>
        )}
      </div>
    );
  },
);
SignalGauge.displayName = "SignalGauge";

export { SignalGauge, getSignalColor, getBarColor, getBarWidth };
